import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useSearchParams, useParams } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import MemberView from './pages/MemberView';
import CheckInPage from './pages/CheckInPage';
import TestQRPage from './pages/TestQRPage';
import ContractSignPage from './pages/ContractSignPage';
import VerifyPage from './pages/VerifyPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import WIAActivate from './pages/WIAActivate';
import WIAAdminDashboard from './pages/WIAAdminDashboard';
import BotLinksAdmin from './pages/BotLinksAdmin';
import CallOverlay from './components/CallOverlay';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Register Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

const AppRouter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const memberId = searchParams.get('member');
  const cardId = searchParams.get('card'); // W Business Card ID (format: wbc-{memberId})
  const tab = searchParams.get('tab');
  const openChat = searchParams.get('openChat');
  
  // Incoming call parameters
  const incomingCallId = searchParams.get('incomingCall');
  const callerName = searchParams.get('callerName');
  
  // State to control call overlay
  const [activeCall, setActiveCall] = useState(null);
  const [checkingCall, setCheckingCall] = useState(false);
  
  // Extract member ID from card ID (wbc-{memberId} format)
  const cardMemberId = cardId?.startsWith('wbc-') ? cardId.replace('wbc-', '') : null;
  
  // PWA Home Screen Fix: Save shared card URL and restore when opened without params
  useEffect(() => {
    const currentUrl = window.location.href;
    
    // If URL has card parameter, save it for PWA Home Screen
    if (cardId && tab === 'wbusiness') {
      localStorage.setItem('wclub_shared_card_url', currentUrl);
      console.log('[App] Saved shared card URL:', currentUrl);
    }
    // If opened without card params but has saved card URL, redirect to it
    else if (!cardId && !tab && !memberId) {
      const savedCardUrl = localStorage.getItem('wclub_shared_card_url');
      if (savedCardUrl && savedCardUrl !== currentUrl) {
        console.log('[App] Restoring saved card URL:', savedCardUrl);
        // Only restore once - clear after redirect
        localStorage.removeItem('wclub_shared_card_url');
        window.location.href = savedCardUrl;
      }
    }
  }, [cardId, tab, memberId]);
  
  // Handle incoming call from URL params - verify call has offer before showing overlay
  useEffect(() => {
    if (incomingCallId && callerName && !checkingCall && !activeCall) {
      console.log('[App] Incoming call from URL, verifying...', incomingCallId);
      setCheckingCall(true);
      
      // Poll until call has offer or times out
      const checkCallReady = async () => {
        const maxAttempts = 20; // 10 seconds max
        for (let i = 0; i < maxAttempts; i++) {
          try {
            const { data } = await axios.get(`${API}/call/${incomingCallId}`);
            console.log(`[App] Call check ${i+1}/${maxAttempts}: offer=${!!data.offer}, status=${data.status}`);
            
            if (data.status === 'ended' || data.status === 'rejected') {
              console.log('[App] Call already ended');
              setSearchParams({});
              setCheckingCall(false);
              return;
            }
            
            if (data.offer) {
              console.log('[App] Call ready with offer, showing overlay');
              setActiveCall({
                id: incomingCallId,
                callerName: decodeURIComponent(callerName),
                calleeName: 'Você',
                isCaller: false
              });
              setSearchParams({});
              setCheckingCall(false);
              return;
            }
            
            // Wait 500ms before next check
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (e) {
            console.log('[App] Error checking call:', e.message);
          }
        }
        
        console.log('[App] Timeout waiting for call offer');
        setSearchParams({});
        setCheckingCall(false);
      };
      
      checkCallReady();
    }
  }, [incomingCallId, callerName, setSearchParams, checkingCall, activeCall]);

  // If URL has ?card=wbc-ID, open W Business card for that member
  if (cardMemberId) {
    return (
      <>
        <Dashboard initialTab="wbusiness" initialMemberId={cardMemberId} />
        {activeCall && (
          <CallOverlay 
            callId={activeCall.id}
            callerName={activeCall.callerName}
            calleeName={activeCall.calleeName}
            isCaller={activeCall.isCaller}
            onEnd={() => setActiveCall(null)}
          />
        )}
      </>
    );
  }

  // If URL has ?member=ID, save it so PWA home screen shortcut remembers
  if (memberId) {
    localStorage.setItem('wclub_member_id', memberId);
    return (
      <>
        <MemberView memberId={memberId} />
        {activeCall && (
          <CallOverlay 
            callId={activeCall.id}
            callerName={activeCall.callerName}
            calleeName={activeCall.calleeName}
            isCaller={activeCall.isCaller}
            onEnd={() => setActiveCall(null)}
          />
        )}
      </>
    );
  }

  // Only use localStorage fallback when opened as installed PWA (standalone mode)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const savedMemberId = localStorage.getItem('wclub_member_id');
  if (isStandalone && savedMemberId && !tab && !incomingCallId) {
    return (
      <>
        <MemberView memberId={savedMemberId} />
        {activeCall && (
          <CallOverlay 
            callId={activeCall.id}
            callerName={activeCall.callerName}
            calleeName={activeCall.calleeName}
            isCaller={activeCall.isCaller}
            onEnd={() => setActiveCall(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Dashboard initialTab={tab} initialOpenChat={openChat} />
      {activeCall && (
        <CallOverlay 
          callId={activeCall.id}
          callerName={activeCall.callerName}
          calleeName={activeCall.calleeName}
          isCaller={activeCall.isCaller}
          onEnd={() => setActiveCall(null)}
        />
      )}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<AppRouter />} />
          <Route path="/member/:memberId" element={<MemberRouteHandler />} />
          <Route path="/card/:cardId" element={<CardRouteHandler />} />
          <Route path="/checkin/:eventId" element={<CheckInPage />} />
          <Route path="/test-qr/:eventId" element={<TestQRPage />} />
          <Route path="/sign/:token" element={<ContractSignPage />} />
          <Route path="/verify/:hash" element={<VerifyPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/wia-activate" element={<WIAActivate />} />
          <Route path="/admin/wia" element={<WIAAdminDashboard />} />
          <Route path="/admin/bot-links" element={<BotLinksAdmin />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

// Handler for /member/:memberId route
const MemberRouteHandler = () => {
  const { memberId } = useParams();
  return <MemberView memberId={memberId} />;
};

// Handler for /card/:cardId route - redirects to Dashboard with card params
const CardRouteHandler = () => {
  const { cardId } = useParams();
  // Extract member ID from card ID (wbc-{memberId} or just memberId)
  const memberId = cardId?.startsWith('wbc-') ? cardId.replace('wbc-', '') : cardId;
  
  // Redirect to main route with card params for PWA compatibility
  useEffect(() => {
    if (memberId) {
      window.location.replace(`/?card=wbc-${memberId}&tab=wbusiness`);
    }
  }, [memberId]);
  
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white/50 font-['Jost']">Carregando cartão...</div>
    </div>
  );
};

export default App;
